import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { format, parseJSON } from 'date-fns';

// Common US timezones for user selection
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKST)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
];

/**
 * Get user's detected timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format a game date and time in the user's timezone
 */
export const formatGameDateTime = (
  gameDate: string,
  gameTime: string,
  gameTimezone: string = 'UTC',
  userTimezone?: string
): {
  date: string;
  time: string;
  fullDateTime: string;
  timeWithZone: string;
} => {
  try {
    const targetTimezone = userTimezone || getUserTimezone();
    
    // Handle invalid or missing data
    if (!gameDate || !gameTime) {
      return {
        date: gameDate || 'TBD',
        time: gameTime || 'TBD', 
        fullDateTime: `${gameDate || 'TBD'} ${gameTime || 'TBD'}`,
        timeWithZone: gameTime || 'TBD'
      };
    }
    
    // Ensure time format includes seconds
    let normalizedTime = gameTime;
    if (!gameTime.includes(':')) {
      normalizedTime = `${gameTime}:00:00`;
    } else if (gameTime.split(':').length === 2) {
      normalizedTime = `${gameTime}:00`;
    }
    
    // For UTC games, we want to preserve the date as-is and only convert the time
    if (gameTimezone === 'UTC') {
      // Parse the date parts to avoid timezone offset issues
      const [year, month, day] = gameDate.split('-').map(Number);
      const [hours, minutes] = normalizedTime.split(':').map(Number);
      
      // Create a UTC date for time conversion only
      const utcDateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      
      // For the date, use the original date string to avoid timezone shifts
      const userDate = format(new Date(year, month - 1, day), 'MMM dd, yyyy');
      const userTime = formatInTimeZone(utcDateTime, targetTimezone, 'h:mm a');
      const userTimeZoneAbbr = formatInTimeZone(utcDateTime, targetTimezone, 'zzz');
      const fullDateTime = `${userDate} ${userTime} ${userTimeZoneAbbr}`;
      
      return {
        date: userDate,
        time: userTime,
        fullDateTime,
        timeWithZone: `${userTime} ${userTimeZoneAbbr}`
      };
    }
    
    // For non-UTC timezones, use the original timezone conversion logic
    const dateTimeString = `${gameDate}T${normalizedTime}`;
    const gameDateTime = fromZonedTime(dateTimeString, gameTimezone);
    
    // Check if the parsed date is valid
    if (isNaN(gameDateTime.getTime())) {
      throw new Error('Invalid date/time combination');
    }
    
    // Format in user's timezone
    const userDate = formatInTimeZone(gameDateTime, targetTimezone, 'MMM dd, yyyy');
    const userTime = formatInTimeZone(gameDateTime, targetTimezone, 'h:mm a');
    const userTimeZoneAbbr = formatInTimeZone(gameDateTime, targetTimezone, 'zzz');
    const fullDateTime = formatInTimeZone(gameDateTime, targetTimezone, 'MMM dd, yyyy h:mm a zzz');
    
    return {
      date: userDate,
      time: userTime,
      fullDateTime,
      timeWithZone: `${userTime} ${userTimeZoneAbbr}`
    };
  } catch (error) {
    console.error('Error formatting game date/time:', error);
    return {
      date: gameDate || 'TBD',
      time: gameTime || 'TBD',
      fullDateTime: `${gameDate || 'TBD'} ${gameTime || 'TBD'}`,
      timeWithZone: gameTime || 'TBD'
    };
  }
};

/**
 * Format deadline times in user's timezone
 */
export const formatDeadlineDateTime = (
  deadlineString: string,
  userTimezone?: string
): {
  date: string;
  time: string;
  fullDateTime: string;
  isOverdue: boolean;
} => {
  try {
    const targetTimezone = userTimezone || getUserTimezone();
    const deadline = parseJSON(deadlineString);
    const now = new Date();
    
    const userDate = formatInTimeZone(deadline, targetTimezone, 'MMM dd, yyyy');
    const userTime = formatInTimeZone(deadline, targetTimezone, 'h:mm a');
    const userTimeZoneAbbr = formatInTimeZone(deadline, targetTimezone, 'zzz');
    const fullDateTime = formatInTimeZone(deadline, targetTimezone, 'MMM dd, yyyy h:mm a zzz');
    
    return {
      date: userDate,
      time: userTime,
      fullDateTime: `${userDate} ${userTime} ${userTimeZoneAbbr}`,
      isOverdue: now > deadline
    };
  } catch (error) {
    console.error('Error formatting deadline:', error);
    return {
      date: deadlineString,
      time: '',
      fullDateTime: deadlineString,
      isOverdue: false
    };
  }
};

/**
 * Convert a time to user's timezone for notifications
 */
export const getNotificationTime = (
  gameDate: string,
  gameTime: string,
  gameTimezone: string = 'UTC',
  userTimezone?: string,
  minutesBefore: number = 30
): Date => {
  try {
    const targetTimezone = userTimezone || getUserTimezone();
    const dateTimeString = `${gameDate}T${gameTime}:00`;
    const gameDateTime = fromZonedTime(dateTimeString, gameTimezone);
    
    // Subtract minutes for notification
    const notificationTime = new Date(gameDateTime.getTime() - (minutesBefore * 60 * 1000));
    
    return notificationTime;
  } catch (error) {
    console.error('Error calculating notification time:', error);
    return new Date();
  }
};

/**
 * Get timezone abbreviation for display
 */
export const getTimezoneAbbreviation = (timezone?: string): string => {
  try {
    const targetTimezone = timezone || getUserTimezone();
    const now = new Date();
    return formatInTimeZone(now, targetTimezone, 'zzz');
  } catch (error) {
    return 'UTC';
  }
};